import { motion } from "motion/react"
import { type ReactNode } from "react"
import { cx } from "@/lib/utils/cx"

// Spring physics — match onboarding-shell.ONBOARDING_SPRING so the visual
// vocabulary stays continuous between flows.
const WELCOME_SPRING = { type: "spring", stiffness: 260, damping: 30, mass: 0.9 } as const

export type WelcomeStep = "password" | "profile" | "tour"

const STEP_ORDER: WelcomeStep[] = ["password", "profile", "tour"]
const STEP_LABEL: Record<WelcomeStep, string> = {
  password: "Set password",
  profile: "Your name",
  tour: "Welcome",
}

export interface WelcomeShellProps {
  step: WelcomeStep
  /** When the profile step is being skipped (name pre-filled by inviter), pass false to hide it from the rail. */
  showProfileStep?: boolean
  /** Persistent badge — drives the "[Team] · [Role]" line at the top. */
  team?: { name: string; role: string }
  /** When true, render without the step rail — used by /invites/:uuid (the
   *  accept screen) which sits before the wizard officially starts. */
  hideProgress?: boolean
  children: ReactNode
}

/**
 * Chrome for the personalised invitee welcome flow. Shared across:
 *   - /invites/:uuid (the accept-invite preview)
 *   - /welcome/password (set initial password)
 *   - /welcome/profile (confirm name)
 *   - /welcome/role-tour (role-aware "here's where to start")
 *
 * The persistent team/role badge is the single source of personalisation —
 * the invitee should feel they never left the invitation through every step.
 */
export function WelcomeShell({ step, showProfileStep = true, team, hideProgress = false, children }: WelcomeShellProps) {
  const visibleSteps = STEP_ORDER.filter((s) => s !== "profile" || showProfileStep)
  const activeIndex = visibleSteps.indexOf(step)

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/30 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-zinc-100/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center p-4 md:p-10">
        <div className="w-full max-w-5xl">
          <Header team={team} />

          {!hideProgress && activeIndex >= 0 && (
            <div className="mb-6 md:mb-8 flex items-center justify-center gap-2">
              {visibleSteps.map((s, i) => {
                const isActive = i === activeIndex
                const isDone = i < activeIndex
                return (
                  <div key={s} className="flex items-center gap-2">
                    <motion.span
                      initial={false}
                      animate={{
                        backgroundColor: isDone || isActive ? "rgb(16 185 129)" : "rgb(228 228 231)",
                        scale: isActive ? 1.2 : 1,
                      }}
                      transition={{ duration: 0.3 }}
                      className="size-2 rounded-full"
                    />
                    <span
                      className={cx(
                        "text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors",
                        isActive ? "text-zinc-900" : isDone ? "text-emerald-600" : "text-zinc-400",
                      )}
                    >
                      {STEP_LABEL[s]}
                    </span>
                    {i < visibleSteps.length - 1 && (
                      <span className="text-zinc-300 mx-1">·</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ ...WELCOME_SPRING, opacity: { duration: 0.25 } }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

function Header({ team }: { team?: { name: string; role: string } }) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="flex items-center justify-center mb-5">
        <div className="inline-flex items-center gap-3">
          <div className="size-12 rounded-2xl bg-zinc-900 grid place-items-center shadow-lg shadow-zinc-900/15">
            <span
              className="text-white font-black text-sm"
              style={{ fontFamily: "var(--font-syne)" }}
            >
              eT<span className="text-emerald-400">X</span>
            </span>
          </div>
          <span
            className="text-base font-bold text-zinc-900 tracking-tight hidden sm:inline"
            style={{ fontFamily: "var(--font-syne)" }}
          >
            Impa<span className="text-emerald-500">X</span>t
          </span>
        </div>
      </div>

      {team && (
        <div className="flex items-center justify-center">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-zinc-200/80 bg-white/70 backdrop-blur-sm px-3.5 py-1.5 shadow-sm">
            <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
            <span className="text-[12.5px] font-semibold text-zinc-900 tracking-tight">
              {team.name}
            </span>
            <span className="text-zinc-300">·</span>
            <span className="text-[12.5px] text-zinc-500">{team.role}</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** Two-column step card matching onboarding's StepLayout vocabulary —
 *  form on the left, hero (or context) on the right. */
export function WelcomeCard({
  eyebrow,
  title,
  subtitle,
  hero,
  children,
}: {
  eyebrow?: string
  title: string
  subtitle?: string
  hero?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
      <div className={cx("grid", hero ? "lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]" : "")}>
        <div className="p-7 md:p-10 lg:p-12">
          <div className="mb-7">
            {eyebrow && (
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
                {eyebrow}
              </p>
            )}
            <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="text-[15px] text-zinc-500 mt-2 leading-relaxed">{subtitle}</p>
            )}
          </div>
          {children}
        </div>

        {hero && (
          <div className="relative hidden lg:block bg-gradient-to-br from-zinc-50 via-white to-emerald-50/40 border-l border-zinc-100 p-10 overflow-hidden">
            <div
              aria-hidden
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgb(212 212 216 / 0.6) 1px, transparent 0)",
                backgroundSize: "16px 16px",
              }}
            />
            <div className="relative h-full flex items-center justify-center">{hero}</div>
          </div>
        )}
      </div>
    </div>
  )
}
